import { useCallback, useEffect, useRef, useState } from 'react'
import { DataTableSelectionChangeParams } from 'primereact/datatable'
import { isFinite } from 'lodash'
import { validate as validateEmail } from 'email-validator'
import { endOfWeek, format, isThisWeek, parse, startOfYesterday, isValid } from 'date-fns'
import { PageLinkCondition, PageLinkConditions } from 'types/pages'
import { SelectedSupplierState, UserType } from 'types/login'

// Builds a HTML element to inject into DOM
export const buildElement = (
  type: string,
  attributes: { [key: string]: string },
  ...children: (HTMLElement | string)[]
) => {
  const newElement = document.createElement(type)

  Object.keys(attributes).forEach((key: string) => {
    newElement.setAttribute(key, attributes[key])
  })

  children.forEach((child) => {
    if (typeof child === 'string') {
      newElement.appendChild(document.createTextNode(child))
    } else if (child instanceof HTMLElement) {
      newElement.appendChild(child)
    }
  })

  return newElement
}

// Strips an array of duplicate values
export const stripDuplicates = <T = any>(inputArr: T[]): T[] => {
  return Array.from(new Set(inputArr))
}

/* Number Related Utilities */

export const roundNumber = (value: number | null | undefined, maxDecimal?: number) => {
  let decimal = '1'

  if (maxDecimal) {
    for (let i = 0; i < maxDecimal; i++) {
      decimal += '0'
    }
  }

  if (typeof value === 'number') {
    return Math.round(value * parseInt(decimal)) / parseInt(decimal)
  }

  return value
}

export type FormatNumberOptions = {
  format?: 'abbr' | '£' | '%' | 'Units' | 'Cases' | 'Days'
  parseString?: boolean | 'forced'
  showPositive?: boolean
  minDP?: number
  maxDP?: number
  suffixWrap?: boolean
}
export const formatNumber = (value: number | string | null | undefined, options?: FormatNumberOptions) => {
  const { format, parseString = true, showPositive, minDP, maxDP, suffixWrap } = options || {}

  const numberValue =
    typeof value === 'number'
      ? value
      : parseString && typeof value === 'string' && (parseString === 'forced' || /^[+-]?\d+(\.\d+)?$/.test(value))
      ? parseFloat(value)
      : null

  if (typeof numberValue === 'number' && isFinite(numberValue)) {
    let finalNumberValue = numberValue

    let abbreviationSymbol = ''
    if (format === 'abbr') {
      const abbreviationFormat = [
        { value: 1e12, symbol: 'T' },
        { value: 1e9, symbol: 'B' },
        { value: 1e6, symbol: 'M' },
        { value: 1e3, symbol: 'K' },
      ].find((item) => Math.abs(numberValue) >= item.value)
      finalNumberValue = numberValue / (abbreviationFormat?.value ?? 1)
      abbreviationSymbol = abbreviationFormat?.symbol ?? ''
    }

    const getOptions = (): Intl.NumberFormatOptions => {
      return {
        ...{
          minimumFractionDigits: minDP,
          maximumFractionDigits: maxDP ?? minDP ?? 1,
        },
        ...(showPositive && { signDisplay: 'exceptZero' }),
        ...(format === '£' && {
          style: 'currency',
          currency: 'GBP',
          minimumFractionDigits: minDP ?? 2,
          maximumFractionDigits: maxDP ?? minDP ?? 2,
        }),
      }
    }

    const getSuffix = () => {
      switch (format) {
        case '%':
          return format
        case 'Cases':
        case 'Units':
        case 'Days':
          return ` ${suffixWrap ? '\n' : ''}${format}`
        case 'abbr':
          return abbreviationSymbol
        default:
          return ''
      }
    }

    return `${finalNumberValue.toLocaleString('en-GB', getOptions())}${getSuffix()}`
  }

  if (typeof value !== 'number') return value
}

// Format a raw size in bytes to appropriate unit with suffix
export const formatBytes = (bytes: number, decimals = 2) => {
  if (bytes === 0) return '0 Bytes'

  const k = 1024
  const dm = decimals < 0 ? 0 : decimals
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']

  const i = Math.floor(Math.log(bytes) / Math.log(k))

  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i]
}

/* Date Related Utilities */

//Parse string into date obj
export const parseDate = (date: string, inputFormat?: string) => {
  return parse(date, inputFormat ?? 'yyyy-MM-dd', new Date())
}

//Parse string into date and time obj
export const parseDateTime = (date: string, time: string, inputFormat?: string) => {
  return parse(date + 'T' + time, inputFormat ?? "yyyy-MM-dd'T'HH:mm", new Date())
}

// Formats a date into any desired format and returns a string
export const formatDateString = (
  date: string | Date | null | undefined,
  inputFormat?: string,
  outputFormat?: string
) => {
  if (date) {
    const dateObj = typeof date === 'string' ? parseDate(date, inputFormat) : date
    const formattedDateString = dateObj.getDate() ? format(dateObj, outputFormat ?? 'dd/MM/yy') : '-'
    return formattedDateString
  }
  return '-'
}

//Returns the start and end date of the week commencing
export const getWeekCommencingDates = (weekCommencing: string, inputFormat?: string) => {
  const weekCommencingStart = parseDate(weekCommencing, inputFormat)
  const weekCommencingEnd = isThisWeek(weekCommencingStart, { weekStartsOn: 1 })
    ? startOfYesterday()
    : endOfWeek(weekCommencingStart, { weekStartsOn: 1 })

  return {
    weekStartDate: format(weekCommencingStart, 'yyyy-MM-dd'),
    weekEndDate: format(weekCommencingEnd, 'yyyy-MM-dd'),
  }
}

/* String Related Utilities */

// Truncates a string to a specific length with a suffix
export const truncateWithSuffix = (input: string, targetLength: number, suffix?: string) => {
  if (input.length > targetLength) {
    return `${input.substring(0, targetLength)}${suffix ?? ''}`
  }
  return input
}

/**
 * Capitalizes the first letter of all the words in a string
 * @param input "lorem ipsum-dolor/sit+amet"
 * @returns "Lorem Ipsum-Dolor/Sit+Amet"
 */
export const capitalizeString = (input: string) => input.replace(/\b[a-z]/g, (match) => match.toUpperCase())

/**
 * Validates an email string
 * @param emailString Email address to test
 * @returns True if valid, false if invalid
 */
export const validateEmailString = (emailString: string, validateMorrisonsEmail?: boolean) => {
  if (validateMorrisonsEmail) {
    return (
      validateEmail(emailString) &&
      (emailString.endsWith('morrisons.com') ||
        emailString.endsWith('morrisons.co.uk') ||
        emailString.endsWith('morrisonsplc.co.uk'))
    )
  }

  return validateEmail(emailString)
}

/**
 * Checks if the reporting period is valid
 * @returns true if valid, false if invalid
 */
export const reportingPeriodCheck = (reportingPeriod: string | undefined, startDate?: string, endDate?: string) => {
  if (reportingPeriod) {
    if (reportingPeriod === 'Custom') {
      if (startDate && isValid(parseDate(startDate)) && endDate && isValid(parseDate(endDate))) return true
    } else {
      return true
    }
  } else if (reportingPeriod === undefined && startDate === undefined && endDate === undefined) {
    return true
  }

  return false
}

/* State Related Utilities */

/**
 * Returns a stateful value, a function to update it, and an optional call back with the new state.
 * IT CANNOT BE USED FOR WAITING ON A STATE CHANGE
 * @param initialState Initial state
 * @example const [state, setState] = useStateWithCallback('Hello ')
 * @example setState((prevState) => prevState + ' World', (newState) => {console.log(newState)}) //Prints "Hello World"
 */
export function useStateWithCallback<S>(
  initialState: S | (() => S)
): [S, (state: React.SetStateAction<S>, callback?: (newState: S) => void) => void] {
  const [state, setState] = useState(initialState)
  const callbackRef = useRef<(newState: S) => void>()

  const setStateCallback = useCallback((state, callback) => {
    callbackRef.current = callback
    setState(state)
  }, [])

  useEffect(() => {
    if (callbackRef?.current) {
      callbackRef.current(state)
    }
    callbackRef.current = undefined
  }, [state])

  return [state, setStateCallback]
}

export type ScrollToResultsOptions = {
  ignoreInlineAlerts?: boolean
  scrollPosition?: ScrollLogicalPosition
}
export const scrollToResults = (resultsRef: React.RefObject<HTMLDivElement>, options?: ScrollToResultsOptions) => {
  const { ignoreInlineAlerts, scrollPosition = 'start' } = options || {}

  setTimeout(() => {
    if (ignoreInlineAlerts || !document.getElementsByClassName('inline-alert').length) {
      resultsRef?.current?.scrollIntoView({
        behavior: 'smooth',
        block: scrollPosition,
      })
    }
  }, 500)
}

/* PrimeReact DataTable Utilities */

//Aids in the selection logic for tables that require multi select/deselect feature
type GetMultiSelectTableValuesParams = {
  event: DataTableSelectionChangeParams
  allValue: string[]
  allText?: string[]
  selectedValue: string[]
  selectedText?: string[]
  stateValue: string[]
  stateText?: string[]
}
export const getMultiSelectTableValues = (
  params: GetMultiSelectTableValuesParams
): { value: string[]; text: string[] } => {
  const { event, allValue, allText, selectedValue, selectedText, stateValue, stateText } = params
  const ctrlKey = (event.originalEvent as any).ctrlKey ?? false

  if (!ctrlKey) {
    if (stateValue.length === 1 && selectedValue.length === 1 && stateValue[0] === selectedValue[0]) {
      return { value: ['all'], text: ['all'] }
    } else {
      return { value: selectedValue, text: selectedText ?? selectedValue }
    }
  } else {
    const filteredValue = stateValue.filter((item) => !allValue.includes(item) && item !== 'all')
    const concatValue = [...filteredValue, ...selectedValue]
    const value = concatValue.length ? concatValue : ['all']

    if (allText && stateText && selectedText) {
      const filteredText = stateText.filter((item) => !allText.includes(item) && item !== 'all')
      const concatText = [...filteredText, ...selectedText]
      const text = concatText.length ? concatText : ['all']

      return { value: value, text: text }
    }

    return { value: value, text: value }
  }
}

// Converts PrimeReact DataTable sortOrder values(-1,0,1) to 'ASC/DESC' and vice-versa
export const convertSortDirection = <T extends number | 'ASC' | 'DESC'>(
  sortDirection?: T
): T extends number ? 'ASC' | 'DESC' : -1 | 0 | 1 => {
  if (typeof sortDirection === 'number') {
    switch (sortDirection) {
      case 1:
        return 'ASC' as any
      case -1:
        return 'DESC' as any
      default:
        return 'ASC' as any
    }
  } else {
    switch (sortDirection) {
      case 'ASC':
        return 1 as any
      case 'DESC':
        return -1 as any
      default:
        return 0 as any
    }
  }
}

/* Character Restriction Validation Utilities */

// Checks if "input" string contains any characters from
// the "charactersToCheck" array at the start or end
export const invalidStartEndCharacters = (input: string, charactersToCheck: string[], onlyCheck?: 'start' | 'end') => {
  for (let i = 0; i < input.length; i++) {
    const c = input[i]
    if (charactersToCheck.includes(c)) {
      if (onlyCheck === 'start') {
        if (i === 0) {
          return true
        }
      } else if (onlyCheck === 'end') {
        if (i === input.length - 1) {
          return true
        }
      } else if (i === 0 || i === input.length - 1) {
        return true
      }
    }
  }
  return false
}

// Checks if the "input" string contains characters other than european letters
//  or any additional characters provided in the "exceptions" array
export const invalidSpecialCharacters = (input: string, exceptions?: string[], allowNumbers?: boolean): boolean => {
  for (let c of input) {
    if (exceptions?.includes(c)) {
      continue
    }
    if (allowNumbers && /^[a-zA-Z0-9\u00c0-\u017e]$/.test(c) === false) return true
    if (!allowNumbers && /^[a-zA-Z\u00c0-\u017e]$/.test(c) === false) {
      return true
    }
  }
  return false
}

// Checks if the "input" string contains a consecutive sequence of any
// characters in the "charactersToCheck" array
export const invalidConsecutiveCharacters = (
  input: string,
  charactersToCheck: string[],
  allowSeparate?: boolean
): boolean => {
  for (let i = 0; i < input.length - 1; i++) {
    const c = input[i]
    if (charactersToCheck.includes(c)) {
      if (
        allowSeparate === true
          ? charactersToCheck.includes(input[i + 1]) && input[i] === input[i + 1]
          : charactersToCheck.includes(input[i + 1])
      ) {
        return true
      }
    }
  }
  return false
}

/* Page Related Utilities */

export const getPageCondition = (params: {
  condition: PageLinkCondition | undefined
  userType: UserType | undefined
  selectedSupplier: SelectedSupplierState | undefined
}): { enabled: boolean; message?: string } => {
  const { condition, userType, selectedSupplier } = params

  if (!condition) return { enabled: true }

  const isUserExempt = (condition: PageLinkCondition[PageLinkConditions]) => {
    if (!!userType && condition?.onlyFor && !condition.onlyFor.includes(userType)) return true
    if (!!userType && condition?.exceptFor?.includes(userType)) return true

    return false
  }

  if (condition['disabled']) {
    if (!isUserExempt(condition['disabled'])) {
      return { enabled: false, message: condition['disabled']?.message }
    }
  }

  if (condition['supplier-selection']) {
    if (!isUserExempt(condition['supplier-selection']) && !selectedSupplier?.oracleId) {
      return { enabled: false, message: condition['supplier-selection']?.message }
    }
  }

  return { enabled: true }
}
